// Dashboard Page — Deux stratégies : Reprendre vs Créer
// Bannière du diagnostic en cours le plus récent + tableau historique avec filtres

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  PlayCircle,
  ArrowRight,
  Loader2,
  Check,
  Trash2,
  RefreshCw,
  ClipboardCheck,
  FileText,
  Award,
  MoreHorizontal,
  Pencil,
  Building2,
  ExternalLink,
} from 'lucide-react'
// Layout is handled by route-level DashboardLayout
import { useNotifications } from '../contexts/NotificationsContext'
import { Card, CardHeader, CardTitle, CardDescription, CardAction, CardContent, CardFooter } from '../components/ui'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Progress } from '../components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import { getCasquesAffiches } from '@autodiag/shared'
import type { DiagnosticHistoryItem } from '../components/dashboard/DiagnosticsHistory'
import { CasquesDisplay } from '../components/dashboard/DiagnosticsHistory'
import { useEntreprise } from '../contexts/EntrepriseContext'
import { useAuth } from '../contexts/AuthContext'
import { api, authApi } from '../services/api'
import { cn } from '../lib/utils'
import { OIDC_ISSUER } from '../lib/env'
import { trackEvent } from '../lib/analytics'

// Calcul de progression pour un diagnostic en cours (affichage barre %)
interface DiagProgression {
  label: string
  percent: number
  nextRoute: string
}

function getProgression(diag: DiagnosticHistoryItem): DiagProgression {
  const count = diag.reponsesCount || 0
  // Distinction null (jamais visité) vs [] (visité, 0 sélections)
  const visitedSolutions = Array.isArray(diag.selectedSolutions)
  const hasSolutions = visitedSolutions && diag.selectedSolutions!.length > 0
  const hasThemes = Array.isArray(diag.selectedThemes) && diag.selectedThemes.length > 0

  if (count === 0) {
    return {
      label: 'Situations a risque',
      percent: 10,
      nextRoute: `/diagnostic/${diag.id}/situations`,
    }
  }

  // Étapes tardives (thèmes/solutions sélectionnés)
  if (hasSolutions) {
    return {
      label: 'Plan d\'actions',
      percent: 95,
      nextRoute: `/diagnostic/${diag.id}/finalisation`,
    }
  }

  // L'utilisateur est passé par /solutions (même avec 0 sélections) → finalisation
  if (visitedSolutions) {
    return {
      label: 'Finalisation',
      percent: 92,
      nextRoute: `/diagnostic/${diag.id}/finalisation`,
    }
  }

  if (hasThemes) {
    return {
      label: 'Solutions de prévention',
      percent: 90,
      nextRoute: `/diagnostic/${diag.id}/solutions`,
    }
  }

  if (diag.resultat) {
    return {
      label: 'Choix des thèmes',
      percent: 85,
      nextRoute: `/diagnostic/${diag.id}/resultats`,
    }
  }

  const nbMetiers = diag.metierIds.length
  const estimatedTotal = 30 * nbMetiers + 30
  const rawPercent = Math.min(80, Math.round((count / estimatedTotal) * 75) + 10)
  return {
    label: 'Questionnaire en cours',
    percent: rawPercent,
    nextRoute: `/diagnostic/${diag.id}/q1`,
  }
}

// Smart routing — analyse les reponses Q1/Q2 pour determiner la bonne page de reprise
function computeResumeRoute(
  diagnosticId: string,
  diagnostic: any
): { route: string; state: Record<string, unknown> } {
  const metierCodes: string[] = (diagnostic.metiers || [])
    .map((m: any) => m.metier?.code || m.metierId)
    .filter(Boolean)
  const metiersSelectionnes = (diagnostic.metiers || []).map((m: any, i: number) => ({
    code: m.metier?.code || m.metierId,
    nom: m.metier?.nom || m.metierId,
    ordre: i + 1,
  }))
  const baseState = { metierIds: metierCodes, metiersSelectionnes }

  const reponses: any[] = diagnostic.reponses || []
  const q1Responses = reponses.filter((r: any) => r.metierId || r.metier)
  const q2Responses = reponses.filter((r: any) => !r.metierId && !r.metier)

  // 1. Terminé → /resultats
  if (diagnostic.statut === 'termine') {
    return { route: `/diagnostic/${diagnosticId}/resultats`, state: baseState }
  }

  // 2. Étapes tardives — vérifier indépendamment de diagnostic.resultat
  // (resultat peut être null si l'utilisateur a navigué via le stepper au lieu de cliquer "Voir les résultats")
  // Distinction null (jamais visité) vs [] (visité mais 0 sélections) :
  //   - null → l'utilisateur n'est pas encore passé par cette étape
  //   - [] → l'utilisateur est passé mais n'a rien sélectionné → avancer, pas boucler
  const visitedSolutions = Array.isArray(diagnostic.selectedSolutions)
  const hasSolutions = visitedSolutions && diagnostic.selectedSolutions.length > 0
  const hasThemes = Array.isArray(diagnostic.selectedThemes) && diagnostic.selectedThemes.length > 0

  if (hasSolutions || visitedSolutions) {
    // L'utilisateur est déjà passé par la page solutions → aller à la finalisation
    return {
      route: `/diagnostic/${diagnosticId}/finalisation`,
      state: { ...baseState, selectedThemes: diagnostic.selectedThemes, selectedSolutions: diagnostic.selectedSolutions },
    }
  }
  if (hasThemes) {
    return {
      route: `/diagnostic/${diagnosticId}/solutions`,
      state: { ...baseState, selectedThemes: diagnostic.selectedThemes },
    }
  }

  // 3. Résultat existant mais pas encore de thèmes → /resultats
  if (diagnostic.resultat) {
    return { route: `/diagnostic/${diagnosticId}/resultats`, state: baseState }
  }

  // 3. Aucune reponse → /situations
  if (reponses.length === 0) {
    return { route: `/diagnostic/${diagnosticId}/situations`, state: baseState }
  }

  // Metiers ayant des reponses Q1
  const metiersWithQ1 = new Set<string>(
    q1Responses.map((r: any) => r.metier?.code || r.metierId).filter(Boolean)
  )

  // 4. Pas de Q2 → encore sur Q1 premier metier
  if (q2Responses.length === 0) {
    return {
      route: `/diagnostic/${diagnosticId}/q1`,
      state: { ...baseState, currentMetierIndex: 0 },
    }
  }

  // 5. Q2 fait → trouver ou on en est dans le flux multi-metier
  // Chercher le premier metier (index >= 1) SANS reponses Q1
  const nextIncomplete = metierCodes.findIndex(
    (code, i) => i >= 1 && !metiersWithQ1.has(code)
  )

  if (nextIncomplete !== -1) {
    // Metier N pas encore commence
    return {
      route: `/diagnostic/${diagnosticId}/q1`,
      state: { ...baseState, currentMetierIndex: nextIncomplete },
    }
  }

  // 6. Tous les Q1+Q2 faits → dernier metier (ecran "termine")
  return {
    route: `/diagnostic/${diagnosticId}/q1`,
    state: { ...baseState, currentMetierIndex: metierCodes.length - 1 },
  }
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { currentEntreprise, currentCompany, currentSiret } = useEntreprise()
  const { refreshNotifications } = useNotifications()
  const { user, refreshAuth } = useAuth()
  const [allDiagnostics, setAllDiagnostics] = useState<DiagnosticHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  // Story 19 : entreprise individuelle = pas d'organisation rattachée au compte SSO
  const hasOrganization = !!user?.ssoCompanies?.[0]?.id
  // Story 18 : pop-in SIRET obligatoire si non renseigné pour l'org courante.
  const [siretPopinValue, setSiretPopinValue] = useState('')
  const [siretPopinError, setSiretPopinError] = useState<string | null>(null)
  const [siretPopinSaving, setSiretPopinSaving] = useState(false)
  const siretMissing = !!user && hasOrganization && !!currentCompany && !currentSiret

  // Reset le champ pop-in à chaque changement d'org courante (évite valeur résiduelle)
  useEffect(() => {
    setSiretPopinValue('')
    setSiretPopinError(null)
  }, [currentCompany?.id])
  // Story 16 : renommage du diagnostic
  const [renameTarget, setRenameTarget] = useState<DiagnosticHistoryItem | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [resumingId, setResumingId] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  useEffect(() => {
    async function loadDiagnostics() {
      setIsLoading(true)
      try {
        const data = await api.getDiagnostics()
        const items: DiagnosticHistoryItem[] = data.map((d: any) => ({
          id: d.id,
          entrepriseId: d.entrepriseId,
          metierIds: (d.metiers || []).map((m: any) => m.metier?.code || m.metierId),
          nom: d.nom ?? null,
          statut: d.statut,
          dateDebut: d.dateDebut || d.createdAt,
          entrepriseNom: d.entreprise?.raisonSociale,
          entrepriseSiret: d.entreprise?.siret ?? null,
          userName: d.user ? `${d.user.prenom} ${d.user.nom}` : undefined,
          metierNoms: (d.metiers || []).map((m: any) => m.metier?.nom),
          resultat: d.resultat,
          attestation: d.attestation || null,
          versionNumber: d.version ? parseFloat(d.version) : 1,
          versionDiag: d.versionDiag ?? 1,
          parentId: d.parentId ?? undefined,
          reponsesCount: d._count?.reponses || 0,
          selectedThemes: d.selectedThemes || undefined,
          selectedSolutions: d.selectedSolutions || undefined,
        }))
        setAllDiagnostics(items)
      } catch (error) {
        console.error('Erreur chargement diagnostics:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadDiagnostics()
  }, [])

  // Filtrer par entreprise courante : SIRET en clé primaire (1 SIRET = 1 entreprise),
  // fallback raisonSociale pour les rows legacy à siret null en BDD.
  const diagnostics = useMemo(() => {
    if (!currentEntreprise) return allDiagnostics
    return allDiagnostics.filter((d) => {
      if (currentSiret && d.entrepriseSiret) return d.entrepriseSiret === currentSiret
      return d.entrepriseNom === currentEntreprise
    })
  }, [allDiagnostics, currentEntreprise, currentSiret])

  const enCours = diagnostics.filter((d) => d.statut === 'en_cours')
  const termines = diagnostics.filter((d) => d.statut === 'termine')
  const isEmpty = diagnostics.length === 0
  const latestEnCours = enCours.length > 0 ? enCours[0] : null

  const handleResume = async (diag: DiagnosticHistoryItem) => {
    trackEvent('reprendre diagnostic')
    const fallbackState = {
      metierIds: diag.metierIds,
      metiersSelectionnes: diag.metierNoms?.map((nom, i) => ({
        code: diag.metierIds[i],
        nom,
        ordre: i + 1,
      })),
    }

    // Fast path : deja termine avec resultats
    if (diag.resultat) {
      navigate(`/diagnostic/${diag.id}/resultats`, { state: fallbackState })
      return
    }

    // Pour les diagnostics en cours, fetch le detail pour smart routing
    setResumingId(diag.id)
    try {
      const fullDiag = await api.getDiagnostic(diag.id)
      const { route, state } = computeResumeRoute(diag.id, fullDiag)
      navigate(route, { state })
    } catch (error) {
      console.error('Erreur chargement diagnostic pour reprise:', error)
      // Fallback vers ancien comportement
      const progression = getProgression(diag)
      navigate(progression.nextRoute, { state: fallbackState })
    } finally {
      setResumingId(null)
    }
  }

  const handleDelete = async (diagnosticId: string) => {
    try {
      await api.deleteDiagnostic(diagnosticId)
      setAllDiagnostics((prev) => prev.filter((d) => d.id !== diagnosticId))
      refreshNotifications()
    } catch (error) {
      console.error('Erreur suppression diagnostic:', error)
    } finally {
      setDeleteTarget(null)
    }
  }

  // Story 18 : enregistrer le SIRET via la pop-in obligatoire (rattaché à l'org courante)
  const handleSavePopinSiret = async () => {
    setSiretPopinError(null)
    const trimmed = siretPopinValue.trim()
    if (!/^\d{14}$/.test(trimmed)) {
      setSiretPopinError('Le SIRET doit contenir exactement 14 chiffres.')
      return
    }
    if (!currentCompany?.id) {
      setSiretPopinError("Aucune organisation sélectionnée.")
      return
    }
    setSiretPopinSaving(true)
    try {
      await authApi.updateSiret(currentCompany.id, trimmed)
      await refreshAuth()
    } catch (e: any) {
      setSiretPopinError(e?.message || "Erreur lors de l'enregistrement")
    } finally {
      setSiretPopinSaving(false)
    }
  }

  // Story 16 : renommer un diagnostic
  const openRename = (diag: DiagnosticHistoryItem) => {
    setRenameTarget(diag)
    setRenameValue(diag.nom ?? '')
  }
  const handleRename = async () => {
    if (!renameTarget) return
    const trimmed = renameValue.trim().slice(0, 100)
    const newNom = trimmed.length > 0 ? trimmed : null
    try {
      const updated = await api.renameDiagnostic(renameTarget.id, newNom)
      setAllDiagnostics(prev =>
        prev.map(d => (d.id === updated.id ? { ...d, nom: updated.nom } : d))
      )
    } catch (error) {
      console.error('Erreur renommage diagnostic:', error)
    } finally {
      setRenameTarget(null)
      setRenameValue('')
    }
  }

  const handleUpdate = async (diag: DiagnosticHistoryItem) => {
    setUpdatingId(diag.id)
    try {
      const child = await api.renewDiagnostic(diag.id)
      const state = {
        metierIds: diag.metierIds,
        metiersSelectionnes: diag.metierNoms?.map((nom, i) => ({
          code: diag.metierIds[i],
          nom,
          ordre: i + 1,
        })),
      }
      navigate(`/diagnostic/${child.id}/situations`, { state })
    } catch (error) {
      console.error('Erreur renouvellement diagnostic:', error)
      alert('Impossible de créer la mise à jour. Vérifiez votre connexion et réessayez.')
    } finally {
      setUpdatingId(null)
    }
  }

  const [, setExportingId] = useState<string | null>(null)

  const handleExportSynthese = async (diag: DiagnosticHistoryItem) => {
    setExportingId(diag.id)
    try {
      await api.downloadRapportPDF(diag.id)
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error)
      alert('Impossible de télécharger la synthèse. Vérifiez votre connexion et réessayez.')
    } finally {
      setExportingId(null)
    }
  }

  const handleAttestation = (diag: DiagnosticHistoryItem) => {
    navigate(`/diagnostic/${diag.id}/attestation`)
  }

  const handleCreateNew = () => {
    if (!hasOrganization) return // story 19 : bloqué si entreprise individuelle
    trackEvent('nouveau diagnostic')
    navigate('/diagnostic/nouveau')
  }

  return (
    <>
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Mes diagnostics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Évaluez vos pratiques de prévention
          </p>
        </div>

        {/* Story 19 — Bandeau bloquant si entreprise individuelle (pas d'organisation rattachée) */}
        {!isLoading && user && !hasOrganization && (
          <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="size-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-amber-900">
                  Aucune entreprise rattachée à votre compte
                </h2>
                <p className="text-sm text-amber-800 mt-1">
                  Diag SCOP Prévention est réservé aux entreprises. Pour créer un diagnostic,
                  vous devez d'abord rattacher votre compte à une entreprise SCOP via PréventionBTP.
                </p>
                <a
                  href={`${OIDC_ISSUER}/ajouter-entreprise`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 hover:underline"
                >
                  Ajouter une entreprise sur PréventionBTP
                  <ExternalLink className="size-3.5" />
                </a>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : isEmpty ? (
          // Story 19 : si pas d'organisation, le bandeau jaune en haut suffit (pas d'EmptyState avec CTA Créer)
          hasOrganization ? <EmptyState onCreateDiagnostic={handleCreateNew} /> : null
        ) : (
          <div className="space-y-6">
            {/* Zone principale : 2 cards cote a cote */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Card 1 : Diagnostic en cours (ou empty state si aucun) */}
              {latestEnCours ? (
                <ResumeCard
                  diagnostic={latestEnCours}
                  onResume={handleResume}
                  isResuming={resumingId === latestEnCours.id}
                />
              ) : (
                <Card className="border-muted bg-muted/30">
                  <CardContent className="p-5 flex flex-col items-center justify-center text-center h-full">
                    <ClipboardCheck className="size-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Aucun diagnostic en cours
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Card 2 : Nouveau diagnostic — dashed + gris + icone +. Désactivée si pas d'organisation (story 19). */}
              <Card
                className={cn(
                  "border-2 border-dashed border-muted-foreground/20 bg-muted/30 transition-colors group",
                  hasOrganization
                    ? "hover:border-muted-foreground/40 hover:bg-muted/50 cursor-pointer"
                    : "opacity-50 cursor-not-allowed"
                )}
                onClick={hasOrganization ? handleCreateNew : undefined}
              >
                <CardContent className="p-5 flex flex-col items-center justify-center text-center h-full">
                  <div className="relative w-24 h-24 mb-3">
                    <EmptyStateIllustration />
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-sm">
                      <Plus className="size-5 text-primary-foreground" />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                    Nouveau diagnostic
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Tableau historique avec filtres */}
            <DiagnosticsTable
              diagnostics={diagnostics}
              enCours={enCours}
              termines={termines}
              onResume={handleResume}
              onUpdate={handleUpdate}
              onExportSynthese={handleExportSynthese}
              onAttestation={handleAttestation}
              onDeleteRequest={setDeleteTarget}
              onRenameRequest={openRename}
              resumingId={resumingId}
              updatingId={updatingId}
            />
          </div>
        )}

        {/* AlertDialog de confirmation de suppression (controlled) */}
        <AlertDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce diagnostic ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Toutes les réponses et résultats
                associés seront définitivement supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteTarget && handleDelete(deleteTarget)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Story 18 — Pop-in SIRET obligatoire (bloque tant que non renseigné) */}
        <AlertDialog open={siretMissing}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Renseignez votre numéro SIRET</AlertDialogTitle>
              <AlertDialogDescription>
                Pour utiliser Diag SCOP Prévention, nous avons besoin du numéro SIRET de
                votre entreprise. Cette information n'est utilisée que dans cet outil et
                ne remontera pas à PréventionBTP. Vous pourrez la modifier plus tard
                depuis votre profil.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <label htmlFor="siret-popin-input" className="text-sm font-medium text-foreground">
                SIRET (14 chiffres)
              </label>
              <input
                id="siret-popin-input"
                type="text"
                inputMode="numeric"
                value={siretPopinValue}
                onChange={(e) => {
                  setSiretPopinValue(e.target.value.replace(/\D/g, '').slice(0, 14))
                  setSiretPopinError(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSavePopinSiret()
                  }
                }}
                placeholder="12345678901234"
                autoFocus
                className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-primary/30"
              />
              {siretPopinError && (
                <p className="mt-1.5 text-xs text-destructive">{siretPopinError}</p>
              )}
              <p className="mt-1.5 text-xs text-muted-foreground">
                {siretPopinValue.length}/14
              </p>
            </div>
            <AlertDialogFooter>
              <AlertDialogAction onClick={handleSavePopinSiret} disabled={siretPopinSaving}>
                {siretPopinSaving ? 'Enregistrement...' : 'Enregistrer'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Story 16 — Modale renommer le diagnostic */}
        <AlertDialog
          open={!!renameTarget}
          onOpenChange={(open) => !open && setRenameTarget(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Renommer le diagnostic</AlertDialogTitle>
              <AlertDialogDescription>
                Donnez un nom personnalisé à ce diagnostic pour mieux le retrouver.
                Laissez vide pour utiliser le libellé par défaut.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              <input
                type="text"
                value={renameValue}
                maxLength={100}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleRename()
                  }
                }}
                placeholder="Ex : Diagnostic 2026 — Bordeaux"
                autoFocus
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                {renameValue.length}/100 caractères
              </p>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setRenameTarget(null)}>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleRename}>Enregistrer</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  )
}

// --- Sous-composants ---

function ResumeCard({
  diagnostic,
  onResume,
  isResuming,
}: {
  diagnostic: DiagnosticHistoryItem
  onResume: (d: DiagnosticHistoryItem) => void
  isResuming?: boolean
}) {
  const progression = getProgression(diagnostic)
  const dateFormatted = new Date(diagnostic.dateDebut).toLocaleDateString(
    'fr-FR',
    { day: 'numeric', month: 'short', year: 'numeric' }
  )

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader>
        <CardTitle className="text-sm truncate flex items-center gap-2">
          {diagnostic.entrepriseNom || 'Mon diagnostic'}
          <Badge variant="secondary" className="text-xs flex-shrink-0">
            Version {diagnostic.versionNumber || 1}
          </Badge>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-1 mt-1">
          {(diagnostic.metierNoms || diagnostic.metierIds).map((name, i) => (
            <Badge key={i} variant="outline" className="text-[10px] font-normal px-1.5 py-0">
              {name}
            </Badge>
          ))}
        </div>
        <CardDescription className="truncate mt-1">
          {dateFormatted}
          {diagnostic.userName && <> · Créé par {diagnostic.userName}</>}
        </CardDescription>
        <CardAction>
          <Badge variant="outline" className="border-primary/30 text-primary text-xs">
            En cours
          </Badge>
        </CardAction>
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-muted-foreground">{progression.label}</span>
          <span className="text-xs font-semibold text-foreground">{progression.percent}%</span>
        </div>
        <Progress value={progression.percent} className="h-1.5" />
      </CardContent>

      <CardFooter className="justify-end">
        <Button size="sm" onClick={() => onResume(diagnostic)} disabled={isResuming}>
          {isResuming ? (
            <>
              <Loader2 className="size-4 mr-1 animate-spin" />
              Chargement...
            </>
          ) : (
            <>
              Reprendre
              <ArrowRight className="size-4 ml-1" />
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  )
}

function DiagnosticsTable({
  diagnostics,
  enCours,
  termines,
  onResume,
  onUpdate,
  onExportSynthese,
  onAttestation,
  onDeleteRequest,
  onRenameRequest,
  resumingId,
  updatingId,
}: {
  diagnostics: DiagnosticHistoryItem[]
  enCours: DiagnosticHistoryItem[]
  termines: DiagnosticHistoryItem[]
  onResume: (d: DiagnosticHistoryItem) => void
  onUpdate: (d: DiagnosticHistoryItem) => void
  onExportSynthese: (d: DiagnosticHistoryItem) => void
  onAttestation: (d: DiagnosticHistoryItem) => void
  onDeleteRequest: (id: string) => void
  onRenameRequest: (d: DiagnosticHistoryItem) => void
  resumingId: string | null
  updatingId: string | null
}) {
  return (
    <Tabs defaultValue="tous">
      <TabsList>
        <TabsTrigger value="tous">
          Tous ({diagnostics.length})
        </TabsTrigger>
        <TabsTrigger value="en_cours">
          En cours ({enCours.length})
        </TabsTrigger>
        <TabsTrigger value="termines">
          Terminés ({termines.length})
        </TabsTrigger>
      </TabsList>

      <TabsContent value="tous">
        <DiagnosticRows
          items={diagnostics}
          onResume={onResume}
          onUpdate={onUpdate}
          onExportSynthese={onExportSynthese}
          onAttestation={onAttestation}
          onDeleteRequest={onDeleteRequest}
          onRenameRequest={onRenameRequest}
          resumingId={resumingId}
          updatingId={updatingId}
        />
      </TabsContent>
      <TabsContent value="en_cours">
        <DiagnosticRows
          items={enCours}
          onResume={onResume}
          onUpdate={onUpdate}
          onExportSynthese={onExportSynthese}
          onAttestation={onAttestation}
          onDeleteRequest={onDeleteRequest}
          onRenameRequest={onRenameRequest}
          resumingId={resumingId}
          updatingId={updatingId}
        />
      </TabsContent>
      <TabsContent value="termines">
        <DiagnosticRows
          items={termines}
          onResume={onResume}
          onUpdate={onUpdate}
          onExportSynthese={onExportSynthese}
          onAttestation={onAttestation}
          onDeleteRequest={onDeleteRequest}
          onRenameRequest={onRenameRequest}
          resumingId={resumingId}
          updatingId={updatingId}
        />
      </TabsContent>
    </Tabs>
  )
}

function DiagnosticRows({
  items,
  onResume,
  onUpdate,
  onExportSynthese,
  onAttestation,
  onDeleteRequest,
  onRenameRequest,
  resumingId,
  updatingId,
}: {
  items: DiagnosticHistoryItem[]
  onResume: (d: DiagnosticHistoryItem) => void
  onUpdate: (d: DiagnosticHistoryItem) => void
  onExportSynthese: (d: DiagnosticHistoryItem) => void
  onAttestation: (d: DiagnosticHistoryItem) => void
  onDeleteRequest: (id: string) => void
  onRenameRequest: (d: DiagnosticHistoryItem) => void
  resumingId: string | null
  updatingId: string | null
}) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        Aucun diagnostic dans cette catégorie.
      </p>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Entreprise</TableHead>
          <TableHead className="w-16">Version</TableHead>
          <TableHead>Créé par</TableHead>
          <TableHead>Métiers</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Statut</TableHead>
          <TableHead>Résultat</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((diag) => {
          const isEnCours = diag.statut === 'en_cours'
          const hasAttestation = (diag.resultat?.casques ?? 0) > 0
          const dateFormatted = new Date(diag.dateDebut).toLocaleDateString(
            'fr-FR',
            { day: 'numeric', month: 'short', year: 'numeric' }
          )

          return (
            <TableRow key={diag.id}>
              <TableCell className="font-medium">
                {diag.nom ? (
                  <div className="flex flex-col">
                    <span className="text-foreground">{diag.nom}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {diag.entrepriseNom || 'Entreprise'}
                    </span>
                  </div>
                ) : (
                  diag.entrepriseNom || 'Entreprise'
                )}
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="text-xs">
                  Version {diag.versionDiag ?? 1}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {diag.userName || '—'}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {(diag.metierNoms || diag.metierIds).map((name, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] font-normal px-1.5 py-0">
                      {name}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {dateFormatted}
              </TableCell>
              <TableCell>
                {isEnCours ? (
                  <Badge variant="outline" className="border-primary/30 text-primary">
                    En cours
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <Check className="size-3 mr-1" />
                    Terminé
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {isEnCours ? (
                  <div className="flex items-center gap-2">
                    <Progress
                      value={getProgression(diag).percent}
                      className="h-1.5 w-20"
                    />
                    <span className="text-xs text-muted-foreground">
                      {getProgression(diag).percent}%
                    </span>
                  </div>
                ) : diag.resultat ? (
                  <div className="flex items-center gap-1">
                    <CasquesDisplay count={getCasquesAffiches(diag.resultat.casques, diag.attestation).casques} />
                    {getCasquesAffiches(diag.resultat.casques, diag.attestation).badgeDore && (
                      <img src="/images/medaille-dore.svg" alt="Badge doré" className="w-5 h-5" />
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground/60">—</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1 justify-end">
                  {isEnCours ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => onResume(diag)}
                        disabled={resumingId === diag.id}
                      >
                        {resumingId === diag.id ? (
                          <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <PlayCircle className="size-3.5 mr-1.5" />
                        )}
                        {resumingId === diag.id ? 'Chargement...' : 'Reprendre'}
                      </Button>
                      <button
                        onClick={() => onDeleteRequest(diag.id)}
                        className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onUpdate(diag)}
                        disabled={updatingId === diag.id}
                        className="gap-1.5 border-green-600 text-green-600 hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-950"
                      >
                        {updatingId === diag.id ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="size-3.5" />
                        )}
                        Mise à jour
                      </Button>
                      {hasAttestation && !diag.attestation && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onAttestation(diag)}
                          className="gap-1.5 border-amber-500 text-amber-600 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-950"
                        >
                          <Award className="size-3.5" />
                          Attestation
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="size-8 p-0">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onExportSynthese(diag)}>
                            <FileText className="size-4 mr-2" />
                            Exporter la synthèse
                          </DropdownMenuItem>
                          {diag.attestation && (
                            <DropdownMenuItem onClick={() => onAttestation(diag)}>
                              <Award className="size-4 mr-2" />
                              Voir l'attestation
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onRenameRequest(diag)}>
                            <Pencil className="size-4 mr-2" />
                            Renommer
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onDeleteRequest(diag.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="size-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

function EmptyState({ onCreateDiagnostic }: { onCreateDiagnostic: () => void }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-8 relative w-48 h-48">
          <EmptyStateIllustration />
        </div>

        <h2 className="text-xl font-semibold mb-2">
          Commencez votre démarche de prévention
        </h2>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          Réalisez votre premier autodiagnostic sécurité pour évaluer
          vos pratiques et identifier vos axes d'amélioration.
        </p>

        <Button size="lg" onClick={onCreateDiagnostic}>
          <Plus className="size-4 mr-2" />
          Créer un diagnostic
        </Button>
      </div>
    </div>
  )
}

function EmptyStateIllustration() {
  return (
    <svg
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <circle cx="100" cy="100" r="90" className="fill-muted/50" />
      <rect x="60" y="40" width="80" height="110" rx="8"
        className="fill-background stroke-border" strokeWidth="2" />
      <rect x="80" y="32" width="40" height="16" rx="4"
        className="fill-muted stroke-border" strokeWidth="2" />
      <circle cx="100" cy="40" r="4" className="fill-background stroke-border" strokeWidth="2" />
      <rect x="76" y="68" width="12" height="12" rx="2"
        className="fill-primary/20 stroke-primary" strokeWidth="1.5" />
      <path d="M79 74 L82 77 L86 71" className="stroke-primary" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="96" y="71" width="32" height="6" rx="3" className="fill-muted" />
      <rect x="76" y="90" width="12" height="12" rx="2"
        className="fill-primary/20 stroke-primary" strokeWidth="1.5" />
      <path d="M79 96 L82 99 L86 93" className="stroke-primary" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="96" y="93" width="28" height="6" rx="3" className="fill-muted" />
      <rect x="76" y="112" width="12" height="12" rx="2"
        className="fill-background stroke-border" strokeWidth="1.5" />
      <rect x="96" y="115" width="35" height="6" rx="3" className="fill-muted" />
      <g transform="translate(130, 120)">
        <path d="M8 28 C8 28 0 28 0 22 L0 16 C0 8 8 2 18 2 C28 2 36 8 36 16 L36 22 C36 28 28 28 28 28 Z"
          className="fill-primary/15 stroke-primary" strokeWidth="1.5" />
        <path d="M4 20 L32 20" className="stroke-primary" strokeWidth="1.5" />
        <rect x="-2" y="26" width="40" height="4" rx="2" className="fill-primary/20 stroke-primary" strokeWidth="1" />
      </g>
      <circle cx="50" cy="55" r="3" className="fill-primary/30" />
      <circle cx="155" cy="65" r="2" className="fill-primary/20" />
      <circle cx="45" cy="130" r="2.5" className="fill-primary/15" />
    </svg>
  )
}
